/**
 * Industry Menu Scroll (Swiper-based)
 * 
 * Uses Swiper.js for horizontal scrolling menu with center-active slide.
 * Automatically highlights the centered item.
 * Only active on mobile/tablet (below 768px).
 * 
 * @author Co-Pilot, mbreiwick
 */
import Swiper from 'swiper/bundle';
import 'swiper/css/bundle';

export default function initIndustryMenuScroll() {
    const swiperEl = document.querySelector(".gf-swiper");

    // Exit if element doesn't exist
    if (!swiperEl) {
        console.warn('Swiper element .gf-swiper not found');
        return;
    }

    // Create counter element if existing counter element does not exist
    const counterEl = swiperEl.querySelector('.swiper-counter');

    if (!counterEl) {
        const counterEl = document.createElement('div');
        counterEl.className = 'swiper-counter';
        swiperEl.parentElement.appendChild(counterEl);
    }

    let swiperInstance = null;
    const breakpoint = window.matchMedia('(max-width: 5000px)'); // Set crazy as temporary fix

    // Initialize or destroy swiper based on screen size
    function handleBreakpoint() {
        if (breakpoint.matches) {
            // Mobile: initialize swiper if not already initialized
            if (!swiperInstance) {
                swiperInstance = initSwiper();
            }
        } else {
            // Desktop: destroy swiper if it exists
            if (swiperInstance) {
                swiperInstance.destroy(true, true);
                swiperInstance = null;

                // Remove all active classes when switching to desktop
                const slides = swiperEl.querySelectorAll('.swiper-slide');
                slides.forEach(slide => {
                    slide.classList.remove('is-center');
                    const link = slide.querySelector(':scope > span > a');
                    if (link) link.classList.remove('--active');
                });
            }
        }
    }

    function initSwiper() {
        const itemCount = document.querySelector('.swiper-wrapper');
        const totalItems = itemCount ? parseInt(itemCount.getAttribute('data-items-count')) : 0;
        const middleSlideIndex = Math.floor(totalItems / 2);
        let totalItemsView = 5;

        if (totalItems <= 5) {
            totalItemsView = 3;
        }

        return new Swiper(swiperEl, {
            initialSlide: middleSlideIndex,
            freeMode: false,
            slidesPerGroup: 1,
            slidesPerView: 1,
            centeredSlides: true,
            // centerInsufficientSlides: false,
            loop: false,
            // loopAdditionalSlides: 1,
            // loopAddBlankSlides: false,
            spaceBetween: 16,
            mousewheel: true,
            cssMode: false,
            navigation: {
                nextEl: ".swiper-button-next",
                prevEl: ".swiper-button-prev",
            },
            breakpoints: {
                466: {
                    slidesPerView: 3,
                },
                800: {
                    slidesPerView: totalItemsView,
                },
                1279: {
                    slidesPerView: totalItemsView,
                }
            },

            // Track active/centered slide
            on: {
                init: function () {
                    updateActiveSlide(this);
                    updateCounter(this);
                },
                slideChange: function () {
                    updateActiveSlide(this);
                    updateCounter(this);
                },
            },
        });
    }


    /**
     * Update Active Slide
     * 
     * Adds 'is-center' class to centered slide and '--active' class to its link.
     * Also shows/hides tooltips for centered/non-centered slides.
     */
    function updateActiveSlide(swiperInstance) {
        const slides = swiperInstance.slides;
        const activeIndex = swiperInstance.activeIndex;

        slides.forEach((slide, index) => {
            const link = slide.querySelector(':scope > span > a');

            if (index === activeIndex) {
                slide.classList.add('is-center');
                if (link) {
                    link.classList.add('--active');
                    link.setAttribute('data-swiper-active', 'true');
                }

                // Show tooltip for centered slide
                showTooltipForSlide(slide);
            } else {
                slide.classList.remove('is-center');
                if (link) {
                    link.classList.remove('--active');
                    link.removeAttribute('data-swiper-active');
                }

                // Hide tooltip for non-centered slides
                hideTooltipForSlide(slide);
            }
        });
    }


    function showTooltipForSlide(slide) {
        const link = slide.querySelector('a[aria-describedby]');
        if (!link) return;

        const tooltipId = link.getAttribute('aria-describedby');
        const tooltip = document.getElementById(tooltipId);

        if (tooltip) {
            tooltip.hidden = false;
            // tooltip.setAttribute('aria-expanded', 'true');
            tooltip.classList.add('--active');
        }

        // Find and activate all elements with the same aria-describedby
        const associatedElements = document.querySelectorAll(`[aria-describedby="${tooltipId}"]`);
        associatedElements.forEach(el => {
            if (!el.closest('[data-flyout-menu]')) {
                el.classList.add('--active');
            }
        });
    }

    function hideTooltipForSlide(slide) {
        const link = slide.querySelector('a[aria-describedby]');
        if (!link) return;

        const tooltipId = link.getAttribute('aria-describedby');
        const tooltip = document.getElementById(tooltipId);

        if (tooltip) {
            tooltip.hidden = true;
            // tooltip.setAttribute('aria-expanded', 'false');
            tooltip.classList.remove('--active');
        }

        // Find and deactivate all elements with the same aria-describedby
        const associatedElements = document.querySelectorAll(`[aria-describedby="${tooltipId}"]`);
        associatedElements.forEach(el => {
            if (!el.closest('[data-flyout-menu]')) {
                el.classList.remove('--active');
            }
        });
    }

    function updateCounter(swiperInstance) {
        // For looped swiper, use realIndex (0-based) + 1
        const current = swiperInstance.realIndex + 1;
        const total = swiperEl.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate):not(.swiper-slide-clone-manual)').length;

        counterEl.textContent = `${current} / ${total}`;
    }

    // Initial check
    handleBreakpoint();

    // Listen for viewport changes
    breakpoint.addEventListener('change', handleBreakpoint);
}